/**
 * @prism/content — game data and its schemas (TRD-02).
 *
 * Imports TYPES ONLY from @prism/sim (R-02-01), so it stays a pure data package and the
 * sim never gains a dependency on it. The daily generator lives in `sim` and receives
 * `GEN_DATA` as a parameter for exactly this reason (TRD-13 R-13-02).
 */
import type { ClassId } from '@prism/sim';
import { CLASSES } from './data/classes.js';
import { ENEMIES } from './data/enemies.js';
import { GEN_DATA } from './data/gen.js';
import { UNLOCK_TREE } from './data/unlocks.js';
import type { UnlockNode } from './schemas/unlocks.js';
import { MAPS, MAP_SOURCES } from './data/maps.js';
import { TOWERS } from './data/towers.js';
import { WAVES } from './data/waves.js';
import type { ClassDef, EnemyDef, MapDef, TowerDef, WaveDef } from './schemas/defs.js';

export * from './schemas/common.js';
export * from './schemas/effects.js';
export * from './schemas/defs.js';
export * from './schemas/unlocks.js';
export * from './color.js';
export * from './autotile.js';
export * from './scenario.js';
export * from './daily.js';
export { CONTENT_HASH } from './fingerprint.js';
export { BUFF_TILES, BUFF_TILES_BY_ID, type BuffTileDef } from './data/buffTiles.js';
export { validateContent, MIN_CLASS_COLOR_DELTA_E } from './validate.js';
export {
  DAILY_UNLOCKS,
  STARTING_CLASSES,
  STARTING_MAPS,
  UNLOCK_TREE,
  unlockedContent,
  unlockedTowerIds,
  type UnlockedContent,
} from './data/unlocks.js';
export type { ValidationResult, ValidationIssue } from './validate.js';

export const CONTENT_VERSION = 1;

/**
 * R-02-02 — content is frozen at module load. Per-run modifications live in `TowerMods`
 * on the entity, never on the def. A mutated def would leak across runs within a session
 * and produce a desync that only reproduces on the second run.
 */
export const classes: readonly ClassDef[] = Object.freeze(CLASSES);
export const towers: readonly TowerDef[] = Object.freeze(TOWERS);
export const enemies: readonly EnemyDef[] = Object.freeze(ENEMIES);
export const waves: readonly WaveDef[] = Object.freeze(WAVES);
export const maps: readonly MapDef[] = Object.freeze(MAPS);
export const mapSources = Object.freeze(MAP_SOURCES);
export const genData = Object.freeze(GEN_DATA);
export const unlockTree: readonly UnlockNode[] = Object.freeze(UNLOCK_TREE);

// ── Lookups ───────────────────────────────────────────────────────────────────────────

const classById = new Map(CLASSES.map((c) => [c.id, c]));
const towerById = new Map(TOWERS.map((t) => [t.id, t]));
const enemyById = new Map(ENEMIES.map((e) => [e.id, e]));
const mapById = new Map(MAPS.map((m) => [m.id, m]));

export function getClass(id: ClassId): ClassDef {
  const def = classById.get(id);
  if (!def) throw new Error(`unknown class "${id}"`);
  return def;
}

export function getTower(id: string): TowerDef {
  const def = towerById.get(id);
  if (!def) throw new Error(`unknown tower "${id}"`);
  return def;
}

/**
 * Non-throwing lookup for data that crossed a version boundary: a leaderboard run
 * stored under an older content set may reference a tower that no longer exists, and
 * a stranger's board must degrade gracefully rather than crash the viewer's menu.
 */
export function getTowerSafe(id: string): TowerDef | undefined {
  return towerById.get(id);
}

export function getEnemy(id: string): EnemyDef {
  const def = enemyById.get(id);
  if (!def) throw new Error(`unknown enemy "${id}"`);
  return def;
}

export function getMap(id: string): MapDef {
  const def = mapById.get(id);
  if (!def) throw new Error(`unknown map "${id}"`);
  return def;
}

/** Towers carrying a class — drives shop filtering and the synergy panel. */
export function towersOfClass(id: ClassId): TowerDef[] {
  return TOWERS.filter((t) => t.classes.includes(id));
}

// ── Balance ───────────────────────────────────────────────────────────────────────────

export { BALANCE, towerCost } from './data/balance.js';

/** TRD-12 §2 — the environment ramp. Nothing may exceed #40 brightness (A-03b). */
export const ENVIRONMENT_PALETTE = Object.freeze({
  void: '#0e0e10',
  outline: '#08080a',
  blocked: '#141416',
  groundDark: '#1a1a1d',
  groundMid: '#232327',
  groundLight: '#2c2c31',
  pathDark: '#34343a',
  pathLight: '#3f3f46',
  uiFrame: '#c8c8d0',
});

export const CLASS_COLORS: Readonly<Record<ClassId, { color: string; accent: string }>> =
  Object.freeze(
    Object.fromEntries(
      CLASSES.map((c) => [c.id, { color: c.color, accent: c.accentColor }]),
    ) as Record<ClassId, { color: string; accent: string }>,
  );
